import { issues as issueContracts } from "@fynd-onshelf/contracts";
import {
  Box,
  MoveHorizontal,
  PackageX,
  Ruler,
  ShieldAlert,
  Tag,
  type LucideIcon,
} from "lucide-react";

import type { IssueInfo, IssueType, SeverityLevel } from "../../lib/api/issues.js";

/**
 * Single source of truth for how an issue is presented — severity colours,
 * ranking, and human-readable type labels.
 *
 * Previously three files carried their own copies that disagreed with each
 * other ("Hidden out of stock" vs "Hidden OOS", "Wrong promo" vs "Promo
 * wrong"), and `compliance/detail.tsx` used a raw `humanize()` that rendered
 * `oos` as **"Oos"** while the list beside it said "Out of stock". Anything
 * that displays an issue should import from here.
 */

/** Sort weight — lower is more urgent. */
export const SEVERITY_RANK: Record<SeverityLevel, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

export interface SeverityStyle {
  /** Left colour band on a list row. */
  band: string;
  /** Small severity chip background + text. */
  chipBg: string;
  chipText: string;
  /** Icon backdrop. */
  iconBg: string;
  iconText: string;
  label: string;
}

export const SEVERITY_STYLES: Record<SeverityLevel, SeverityStyle> = {
  critical: {
    band: "bg-rose-500",
    chipBg: "bg-rose-500/10",
    chipText: "text-rose-600",
    iconBg: "bg-rose-500/10",
    iconText: "text-rose-600",
    label: "Critical",
  },
  high: {
    band: "bg-orange-500",
    chipBg: "bg-orange-500/10",
    chipText: "text-orange-600",
    iconBg: "bg-orange-500/10",
    iconText: "text-orange-600",
    label: "High",
  },
  medium: {
    band: "bg-amber-500",
    chipBg: "bg-amber-500/10",
    chipText: "text-amber-600",
    iconBg: "bg-amber-500/10",
    iconText: "text-amber-600",
    label: "Medium",
  },
  low: {
    band: "bg-sky-500",
    chipBg: "bg-sky-500/10",
    chipText: "text-sky-600",
    iconBg: "bg-sky-500/10",
    iconText: "text-sky-600",
    label: "Low",
  },
};

export const ISSUE_TYPE_ICON: Record<IssueType, LucideIcon> = {
  oos: PackageX,
  hidden_oos: PackageX,
  price_mismatch: Tag,
  missing_price_tag: Tag,
  extra_price_tag: Tag,
  promo_missing: Tag,
  promo_wrong: Tag,
  posm_missing: ShieldAlert,
  planogram_mismatch: Ruler,
  missing_facing: Box,
  wrong_placement: Ruler,
  extra_product: Box,
  low_share_of_shelf: Ruler,
  quality_issue: MoveHorizontal,
  unknown: ShieldAlert,
};

/**
 * Plain-English type labels for store staff. Deliberately avoids internal
 * vocabulary — "oos" reads as "Out of stock", not "Oos".
 */
export const ISSUE_TYPE_LABEL: Record<IssueType, string> = {
  oos: "Empty space",
  hidden_oos: "Hidden empty space",
  price_mismatch: "Price mismatch",
  missing_price_tag: "Missing price tag",
  extra_price_tag: "Extra price tag",
  promo_missing: "Promo missing",
  promo_wrong: "Wrong promo",
  posm_missing: "POSM missing",
  planogram_mismatch: "Planogram mismatch",
  missing_facing: "Missing facing",
  wrong_placement: "Wrong placement",
  extra_product: "Extra product",
  low_share_of_shelf: "Low share of shelf",
  quality_issue: "Quality issue",
  unknown: "Issue",
};

/** Statuses a person can still act on — what a worklist should show. */
export const ACTIONABLE_STATUSES = issueContracts.ACTIONABLE_ISSUE_STATUSES;

export function isActionable(issue: IssueInfo): boolean {
  return (ACTIONABLE_STATUSES as readonly string[]).includes(issue.status);
}

/**
 * Most urgent first, then longest-waiting. Mirrors the ordering the issues
 * list uses server-side when `sort=urgency`.
 */
export function byUrgency(a: IssueInfo, b: IssueInfo): number {
  const rank = (i: IssueInfo): number => SEVERITY_RANK[i.severity] ?? 99;
  const s = rank(a) - rank(b);
  if (s !== 0) return s;
  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
}
